import { Injectable } from '@angular/core';
import { gql } from 'apollo-angular';
import { UserDraft } from 'src/app/shared/models/gql/user-draft.model';
import { draftJobKind } from 'src/app/shared/types/draft-job-kind';
import { ApiInterfaceService } from './api-interface.service';

// User draft mutation input type
// action - create, update, delete
// attributes - User draft details for create, update, delete
type UserDraftsAddDeleteUpdateInput = { action: 'create' | 'update' | 'delete'; attributes: UserDraft };

@Injectable({
  providedIn: 'root',
})
export class UserDraftService {
  constructor(private api: ApiInterfaceService) {}

  /**
   * Sends a request to create/update/delete user draft.
   *
   * @param userDraftCreateDeleteUpdateInput - Contains user draft action(create/update/delete) and user draft object.
   */
  async createUpdateDeleteUserDraft(
    userDraftCreateDeleteUpdateInput: UserDraftsAddDeleteUpdateInput,
  ): Promise<UserDraft> {
    try {
      const resUserDraft = await this.api.gqlMutation(
        gql`
          mutation ($userDraftCreateDeleteUpdateInput: UserDraftCreateDeleteUpdateInput!) {
            userDraft(input: $userDraftCreateDeleteUpdateInput) {
              userDraft {
                id
                kind
                content
              }
            }
          }
        `,
        { userDraftCreateDeleteUpdateInput },
      );

      return resUserDraft['userDraft']['userDraft'];
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Retrieve posted job data from user draft.
   *
   * @param kind - This should be postedJob
   * @param id - draft id
   */
  async retrieveDraft(kind: draftJobKind, id?: string): Promise<UserDraft> {
    try {
      const res = await this.api.gqlQuery(
        gql`
          query ($kind: UserDraftKind!, $id: ID) {
            userDraft(kind: $kind, id: $id) {
              id
              kind
              content
            }
          }
        `,
        { kind, id },
      );

      return res['userDraft'];
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Retrieve multiple posted job data from user draft.
   *
   * @param kind - postedJobPublic
   */
  async retrieveDrafts(kind: draftJobKind): Promise<UserDraft[]> {
    try {
      const res = await this.api.gqlQuery(
        gql`
          query ($kind: UserDraftKind!) {
            userDrafts(kind: $kind) {
              id
              kind
              content
              createdAt
              updatedAt
              postedJob {
                title
                supportDetails {
                  name
                }
              }
            }
          }
        `,
        { kind },
      );

      return res['userDrafts'];
    } catch (error) {
      return Promise.reject(error);
    }
  }
}
